// Normalized API error. The server answers { error, message, fields? } with a
// stable code and a human Spanish message (ADR-0013 §3, ADR-0015); the UI
// shows that message and never raw status codes or internals.

export interface FieldProblem {
  readonly path: string;
  readonly problem: string;
}

export class ApiProblem extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
    readonly fields: readonly FieldProblem[] = [],
    /** The request's id, for support: it locates the failure in the server's log, and reveals nothing. */
    public requestId: string | null = null
  ) {
    super(message);
    this.name = "ApiProblem";
  }

  /** Field-level messages keyed by top-level field name (e.g. "name", "deliveryConfig"). */
  fieldErrors(): Record<string, string> {
    const out: Record<string, string> = {};
    for (const f of this.fields) {
      const key = f.path.replace(/^query\./, "").split(/[.[]/)[0] ?? f.path;
      out[key] ??= problemMessage(f.problem);
    }
    return out;
  }
}

const PROBLEM_MESSAGES: Record<string, string> = {
  REQUIRED: "Este campo es obligatorio.",
  TOO_SHORT: "Es demasiado corto.",
  TOO_LONG: "Es demasiado largo.",
  INVALID_VALUE: "El valor no es válido.",
  INVALID_TYPE: "El valor no es válido.",
  UNKNOWN_FIELD: "Campo no admitido.",
  SECRET_LIKE_KEY: "No incluyas credenciales ni secretos en la configuración.",
  EXECUTABLE_CONTENT_NOT_ALLOWED: "Ese tipo de contenido no está permitido.",
};

export const problemMessage = (problem: string) => PROBLEM_MESSAGES[problem] ?? "Revisa este dato.";

export const GENERIC_ERROR = "Ocurrió un problema inesperado. Inténtalo de nuevo.";
export const OFFLINE_ERROR = "No pudimos conectar con InteractAds. Revisa tu conexión e inténtalo de nuevo.";

/**
 * Builds the problem from the error body ALREADY parsed by the HTTP client
 * (openapi-fetch consumes the response body; reading it again would fail and
 * silently replace the server's human message with a generic one).
 */
export function problemFrom(status: number, body: unknown): ApiProblem {
  if (typeof body === "object" && body !== null && "error" in body) {
    const b = body as { error: unknown; message?: unknown; fields?: unknown };
    return new ApiProblem(
      status,
      String(b.error),
      typeof b.message === "string" ? b.message : GENERIC_ERROR,
      Array.isArray(b.fields) ? (b.fields as FieldProblem[]) : []
    );
  }
  return new ApiProblem(status, status >= 500 ? "INTERNAL_ERROR" : "UNKNOWN", GENERIC_ERROR);
}
